/* eslint-disable react-hooks/exhaustive-deps, no-unused-vars */
import { useState, useRef, useEffect } from 'react'
import { supabase } from '../supabase'


const DEFAULT_WORKSTREAMS = [
  { name: 'Governance', colour: '1A4035', sort_order: 0 },
  { name: 'Solution', colour: '2D6A4F', sort_order: 1 },
  { name: 'Response', colour: '3D8B6A', sort_order: 2 },
  { name: 'Pricing', colour: 'C9A84C', sort_order: 3 },
  { name: 'Commercial & Legal', colour: '4A5568', sort_order: 4 },
]

const DEFAULT_MILESTONES = {
  'Governance': [
    { name: 'Bid / No Bid Decision', is_key_milestone: true, sort_order: 0, rule_days: 30 },
    { name: 'Kick-off Meeting', is_key_milestone: true, sort_order: 1, rule_days: 28 },
    { name: 'Solution Review', is_key_milestone: true, sort_order: 2, rule_days: 20 },
    { name: 'Technical Review', is_key_milestone: true, sort_order: 3, rule_days: 14 },
    { name: 'Clarification Questions Deadline', is_key_milestone: true, sort_order: 4, rule_days: 15 },
    { name: 'Commercial Review', is_key_milestone: true, sort_order: 5, rule_days: 12 },
    { name: 'Red Team Review', is_key_milestone: true, sort_order: 6, rule_days: 10 },
    { name: 'Final Approval', is_key_milestone: true, sort_order: 7, rule_days: 5 },
    { name: 'Submission Deadline', is_key_milestone: true, sort_order: 8, rule_days: 0 },
    { name: 'Result Expected', is_key_milestone: true, sort_order: 9, rule_days: -20 },
  ],
  'Solution': [
    { name: 'Solution Kick-off', is_key_milestone: false, sort_order: 0, rule_days: 28 },
    { name: 'Technical Approach Draft', is_key_milestone: false, sort_order: 1, rule_days: 21 },
    { name: 'Delivery Model Agreed', is_key_milestone: false, sort_order: 2, rule_days: 18 },
    { name: 'Mobilisation Plan Draft', is_key_milestone: false, sort_order: 3, rule_days: 16 },
    { name: 'Key Personnel Confirmed', is_key_milestone: false, sort_order: 4, rule_days: 14 },
    { name: 'TUPE Assessment Complete', is_key_milestone: false, sort_order: 5, rule_days: 12 },
    { name: 'Solution Sign-off', is_key_milestone: true, sort_order: 6, rule_days: 10 },
  ],
  'Response': [
    { name: 'Question Analysis Complete', is_key_milestone: false, sort_order: 0, rule_days: 26 },
    { name: 'Storyboards Complete', is_key_milestone: false, sort_order: 1, rule_days: 22 },
    { name: 'Clarification Questions Submitted', is_key_milestone: true, sort_order: 2, rule_days: 15 },
    { name: 'First Draft Complete', is_key_milestone: false, sort_order: 3, rule_days: 12 },
    { name: 'Review Comments Incorporated', is_key_milestone: false, sort_order: 4, rule_days: 8 },
    { name: 'Final Draft Complete', is_key_milestone: false, sort_order: 5, rule_days: 6 },
    { name: 'Proofread & Formatted', is_key_milestone: false, sort_order: 6, rule_days: 3 },
    { name: 'Response Locked', is_key_milestone: true, sort_order: 7, rule_days: 1 },
  ],
  'Pricing': [
    { name: 'Pricing Assumptions Agreed', is_key_milestone: false, sort_order: 0, rule_days: 25 },
    { name: 'Cost Model Draft', is_key_milestone: false, sort_order: 1, rule_days: 18 },
    { name: 'Cost Model Review', is_key_milestone: false, sort_order: 2, rule_days: 14 },
    { name: 'Commercial Review', is_key_milestone: true, sort_order: 3, rule_days: 12 },
    { name: 'Price Sign-off', is_key_milestone: true, sort_order: 4, rule_days: 5 },
  ],
  'Commercial & Legal': [
    { name: 'Contract Review Start', is_key_milestone: false, sort_order: 0, rule_days: 25 },
    { name: 'Risk Register Draft', is_key_milestone: false, sort_order: 1, rule_days: 20 },
    { name: 'Terms Issues Identified', is_key_milestone: false, sort_order: 2, rule_days: 15 },
    { name: 'Legal Sign-off', is_key_milestone: true, sort_order: 3, rule_days: 7 },
  ],
}

const PX_PER_DAY = 32
const NAME_COL = 220
const STATUS_COL = 120
const ROW_H = 40
const HEADER_H1 = 32
const HEADER_H2 = 28

function addWorkingDays(date, days) {
  const result = new Date(date)
  if (days === 0) return result
  const direction = days > 0 ? 1 : -1
  let remaining = Math.abs(days)
  while (remaining > 0) {
    result.setDate(result.getDate() + direction)
    const day = result.getDay()
    if (day !== 0 && day !== 6) remaining--
  }
  return result
}

function subtractWorkingDays(date, days) {
  return addWorkingDays(date, -days)
}

function formatDate(date) {
  if (!date) return '—'
  return new Date(date).toLocaleDateString('en-GB', {
    day: 'numeric', month: 'short', year: 'numeric'
  })
}

function formatDateShort(date) {
  return new Date(date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
}

function formatDateInput(date) {
  if (!date) return ''
  return new Date(date).toISOString().split('T')[0]
}

function getStatusColour(status, dueDate) {
  if (status === 'complete') return 'var(--green)'
  if (status === 'in_progress') return 'var(--gold)'
  if (status === 'at_risk') return '#E67E22'
  if (status === 'missed') return 'var(--red)'
  if (dueDate && new Date(dueDate) < new Date() && status !== 'complete') return 'var(--red)'
  return 'var(--silver)'
}

function getDaysUntil(date) {
  if (!date) return null
  return Math.ceil((new Date(date) - new Date()) / (1000 * 60 * 60 * 24))
}

function daysBetween(a, b) {
  return Math.ceil((new Date(b) - new Date(a)) / (1000 * 60 * 60 * 24))
}


// ── Gantt timeline component ───────────────────────────────────────────────
function GanttTimeline({
  workstreams, milestones, chartStart, chartEnd,
  onStatusUpdate, onDeleteMilestone, onAddMilestone,
  onRemoveWorkstream, editingMilestone, setEditingMilestone,
  addingMilestoneTo, setAddingMilestoneTo,
  newMilestones, setNewMilestones, onMilestoneDateChanged
}) {
  const scrollRef = useRef(null)

  const paddedStart = new Date(chartStart)
  paddedStart.setDate(paddedStart.getDate() - 3)
  const paddedEnd = new Date(chartEnd)
  paddedEnd.setDate(paddedEnd.getDate() + 7)

  const totalDays = daysBetween(paddedStart, paddedEnd)
  const chartWidth = totalDays * PX_PER_DAY

  const dateToX = (date) => daysBetween(paddedStart, new Date(date)) * PX_PER_DAY

  const todayX = dateToX(new Date())
  const submissionX = dateToX(chartEnd)

  const DATE_COL = 96

  // Week markers
  const weekMarkers = []
  const wCursor = new Date(paddedStart)
  while (wCursor.getDay() !== 1) wCursor.setDate(wCursor.getDate() + 1)
  while (wCursor <= paddedEnd) {
    weekMarkers.push({
      x: dateToX(wCursor),
      label: wCursor.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
    })
    wCursor.setDate(wCursor.getDate() + 7)
  }

  // Month markers
  const monthMarkers = []
  const mCursor = new Date(paddedStart.getFullYear(), paddedStart.getMonth(), 1)
  while (mCursor <= paddedEnd) {
    const x = dateToX(mCursor)
    if (x >= 0) monthMarkers.push({
      x,
      label: mCursor.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' }),
      width: new Date(mCursor.getFullYear(), mCursor.getMonth() + 1, 0).getDate() * PX_PER_DAY
    })
    mCursor.setMonth(mCursor.getMonth() + 1)
  }

  useEffect(() => {
    if (scrollRef.current && todayX > 0) {
      scrollRef.current.scrollLeft = Math.max(0, todayX - 200)
    }
  }, [todayX])

  // Helper — grid lines and today/submission overlays rendered inside every row
  const GridLines = ({ height, bg }) => (
    <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
      {weekMarkers.map((wm, i) => (
        <div key={i} style={{
          position: 'absolute', left: wm.x, top: 0,
          width: 1, height,
          background: 'var(--green-light)', opacity: 0.45
        }} />
      ))}
      {todayX >= 0 && todayX <= chartWidth && (
        <div style={{
          position: 'absolute', left: todayX, top: 0,
          width: 2, height,
          background: 'var(--gold)', opacity: 0.35, zIndex: 1
        }} />
      )}
      <div style={{
        position: 'absolute', left: submissionX, top: 0,
        width: 2, height,
        background: 'var(--green)', opacity: 0.3, zIndex: 1
      }} />
    </div>
  )

  return (
    <div style={{
      position: 'relative',
      border: '1px solid var(--green-light)',
      background: 'var(--white)',
      overflow: 'hidden'
    }}>
      <div style={{ display: 'flex' }}>

        {/* ── LEFT FROZEN — Name column ─────────────────────────────────── */}
        <div style={{
          width: NAME_COL, minWidth: NAME_COL, flexShrink: 0,
          borderRight: '1px solid var(--green-light)',
          zIndex: 10, background: 'var(--white)'
        }}>
          {/* Month header */}
          <div style={{
            height: HEADER_H1, background: 'var(--green-dark)',
            borderBottom: '1px solid var(--green)',
            display: 'flex', alignItems: 'center', padding: '0 12px'
          }}>
            <span style={{
              fontSize: '0.6875rem', fontWeight: 700,
              letterSpacing: '0.08em', textTransform: 'uppercase',
              color: 'var(--green-light)'
            }}>
              Milestone
            </span>
          </div>
          {/* Week header */}
          <div style={{
            height: HEADER_H2, background: 'var(--off-white)',
            borderBottom: '2px solid var(--green-light)'
          }} />

          {workstreams.map(ws => {
            const wsMilestones = milestones
              .filter(m => m.workstream_id === ws.id)
              .sort((a, b) => new Date(a.due_date) - new Date(b.due_date))
            const isAddingHere = addingMilestoneTo === ws.id
            return (
              <div key={ws.id}>
                {/* Workstream header */}
                <div style={{
                  height: ROW_H, display: 'flex', alignItems: 'center',
                  padding: '0 12px', gap: 8,
                  background: `#${ws.colour}18`,
                  borderBottom: '1px solid var(--green-light)'
                }}>
                  <div style={{ width: 8, height: 8, background: `#${ws.colour}`, flexShrink: 0 }} />
                  <span style={{
                    fontSize: '0.6875rem', fontWeight: 700,
                    letterSpacing: '0.1em', textTransform: 'uppercase',
                    color: `#${ws.colour}`,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                  }}>
                    {ws.name}
                  </span>
                </div>
                {/* Add milestone label */}
                {isAddingHere && (
                  <div style={{
                    height: ROW_H + 8, borderBottom: '1px solid var(--green-light)',
                    background: 'var(--green-pale)',
                    display: 'flex', alignItems: 'center', padding: '0 12px'
                  }}>
                    <span style={{ fontSize: '0.75rem', color: 'var(--green-dark)', fontWeight: 600 }}>
                      New milestone
                    </span>
                  </div>
                )}
                {/* Empty state */}
                {wsMilestones.length === 0 && !isAddingHere && (
                  <div style={{
                    height: ROW_H, borderBottom: '1px solid var(--green-light)',
                    display: 'flex', alignItems: 'center', padding: '0 12px'
                  }}>
                    <span style={{ fontSize: '0.75rem', color: 'var(--silver)', fontStyle: 'italic' }}>
                      No milestones
                    </span>
                  </div>
                )}
                {/* Milestone names */}
                {wsMilestones.map((ms, msIdx) => {
                  const isOverdue = new Date(ms.due_date) < new Date() && ms.status !== 'complete'
                  const isEditing = editingMilestone === ms.id
                  return (
                    <div key={ms.id}>
                      <div style={{
                        height: ROW_H, display: 'flex', alignItems: 'center',
                        padding: '0 8px 0 12px', gap: 6,
                        background: msIdx % 2 === 0 ? 'var(--white)' : 'var(--off-white)',
                        borderBottom: '1px solid var(--green-light)'
                      }}>
                        {ms.is_key_milestone && (
                          <span style={{
                            color: isOverdue ? 'var(--red)' : `#${ws.colour}`,
                            fontSize: '0.5rem', flexShrink: 0
                          }}>◆</span>
                        )}
                       <span style={{
                          fontSize: '0.8125rem',
                          color: ms.status === 'missed' ? 'var(--red)' : ms.status === 'at_risk' ? '#E67E22' : isOverdue ? 'var(--red)' : 'var(--charcoal)',
                          fontWeight: ms.is_key_milestone ? 600 : 400,
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                        }}>
                          {ms.name}
                        </span>
                      </div>
                      {/* Edit spacer */}
                      {isEditing && (
                        <div style={{
                          height: ROW_H, borderBottom: '1px solid var(--green-light)',
                          background: 'var(--green-pale)',
                          display: 'flex', alignItems: 'center', padding: '0 12px'
                        }}>
                          <span style={{ fontSize: '0.75rem', color: 'var(--green-dark)', fontWeight: 600 }}>
                            Edit date
                          </span>
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            )
          })}
          <div style={{ height: 20, borderTop: '1px solid var(--green-light)', background: 'var(--off-white)' }} />
        </div>

        {/* ── SCROLLABLE MIDDLE PANE ────────────────────────────────────── */}
        <div style={{ flex: 1, overflow: 'hidden', minWidth: 0 }}>
          <div ref={scrollRef} style={{ overflowX: 'auto', overflowY: 'hidden' }}>
            <div style={{ width: chartWidth, position: 'relative' }}>

              {/* Month header */}
              <div style={{
                height: HEADER_H1, background: 'var(--green-dark)',
                borderBottom: '1px solid var(--green)',
                position: 'relative', overflow: 'hidden'
              }}>
                {monthMarkers.map((m, i) => (
                  <div key={i} style={{
                    position: 'absolute', left: m.x, top: 0,
                    width: m.width, height: HEADER_H1,
                    display: 'flex', alignItems: 'center', paddingLeft: 10,
                    fontSize: '0.75rem', fontWeight: 700,
                    color: 'var(--white)', letterSpacing: '0.04em',
                    whiteSpace: 'nowrap', overflow: 'hidden',
                    borderLeft: i > 0 ? '1px solid rgba(255,255,255,0.15)' : 'none'
                  }}>
                    {m.label}
                  </div>
                ))}
                <div style={{
                  position: 'absolute', left: submissionX, top: 0,
                  width: 2, height: HEADER_H1,
                  background: 'var(--green-light)', opacity: 0.6
                }} />
              </div>

              {/* Week header */}
              <div style={{
                height: HEADER_H2, background: 'var(--off-white)',
                borderBottom: '2px solid var(--green-light)',
                position: 'relative'
              }}>
                {weekMarkers.map((wm, i) => (
                  <div key={i} style={{
                    position: 'absolute', left: wm.x, top: 0,
                    height: HEADER_H2, display: 'flex', alignItems: 'center',
                    paddingLeft: 4, fontSize: '0.6875rem', color: 'var(--slate)',
                    borderLeft: '1px solid var(--green-light)', whiteSpace: 'nowrap'
                  }}>
                    {wm.label}
                  </div>
                ))}
                {todayX >= 0 && todayX <= chartWidth && (
                  <div style={{
                    position: 'absolute', left: todayX, top: 0,
                    width: 2, height: HEADER_H2,
                    background: 'var(--gold)', opacity: 0.8
                  }} />
                )}
                <div style={{
                  position: 'absolute', left: submissionX, top: 0,
                  width: 2, height: HEADER_H2,
                  background: 'var(--green)', opacity: 0.6
                }} />
              </div>

              {/* Workstream + milestone rows */}
              {workstreams.map(ws => {
                const wsMilestones = milestones
                  .filter(m => m.workstream_id === ws.id)
                  .sort((a, b) => new Date(a.due_date) - new Date(b.due_date))
                const isAddingHere = addingMilestoneTo === ws.id
                const nm = newMilestones[ws.id] || { name: '', start_date: '', due_date: '', is_key_milestone: false }

                return (
                  <div key={ws.id}>

                    {/* Workstream background row */}
                    <div style={{
                      height: ROW_H, position: 'relative',
                      background: `#${ws.colour}18`,
                      borderBottom: '1px solid var(--green-light)'
                    }}>
                      <GridLines height={ROW_H} />
                    </div>

                {/* Add milestone form */}
                    {isAddingHere && (
                      <div style={{
                        borderBottom: '1px solid var(--green-light)',
                        background: 'var(--green-pale)',
                        padding: '8px 12px', display: 'flex',
                        flexDirection: 'column', gap: 6,
                        width: NAME_COL, boxSizing: 'border-box'
                      }}>
                        {/* Row 1 — name */}
                        <input
                          type="text" value={nm.name}
                          onChange={e => setNewMilestones(prev => ({
                            ...prev, [ws.id]: { ...prev[ws.id], name: e.target.value }
                          }))}
                          placeholder="Milestone name"
                          style={{
                            width: '100%', padding: '5px 10px',
                            border: '1px solid var(--silver)', fontSize: '0.875rem',
                            borderRadius: 0, outline: 'none', boxSizing: 'border-box'
                          }}
                          onKeyDown={e => e.key === 'Enter' && onAddMilestone(ws.id)}
                          autoFocus
                        />
                        {/* Row 2 — dates, key milestone, buttons */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <span style={{ fontSize: '0.625rem', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--green)' }}>Start (optional)</span>
                            <input
                              type="date" value={nm.start_date || ''}
                              onChange={e => setNewMilestones(prev => ({
                                ...prev, [ws.id]: { ...prev[ws.id], start_date: e.target.value }
                              }))}
                              style={{ padding: '4px 8px', border: '1px solid var(--silver)', fontSize: '0.8125rem', borderRadius: 0, outline: 'none' }}
                            />
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <span style={{ fontSize: '0.625rem', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--green)' }}>End / Due Date *</span>
                            <input
                              type="date" value={nm.due_date}
                              onChange={e => setNewMilestones(prev => ({
                                ...prev, [ws.id]: { ...prev[ws.id], due_date: e.target.value }
                              }))}
                              style={{ padding: '4px 8px', border: '1px solid var(--silver)', fontSize: '0.8125rem', borderRadius: 0, outline: 'none' }}
                            />
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginLeft: 4, marginTop: 14 }}>
                            <input
                              type="checkbox"
                              id={`key-ms-${ws.id}`}
                              checked={nm.is_key_milestone || false}
                              onChange={e => setNewMilestones(prev => ({
                                ...prev, [ws.id]: { ...prev[ws.id], is_key_milestone: e.target.checked }
                              }))}
                              style={{ width: 14, height: 14, cursor: 'pointer' }}
                            />
                            <label htmlFor={`key-ms-${ws.id}`} style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--green-dark)', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                              Key Milestone
                            </label>
                          </div>
                          <div style={{ display: 'flex', gap: 6, marginLeft: 'auto', marginTop: 14 }}>
                            <button
                              onClick={() => onAddMilestone(ws.id)}
                              disabled={!nm.name.trim() || !nm.due_date}
                              style={{
                                background: 'var(--green)', border: 'none',
                                color: 'var(--white)', padding: '5px 14px',
                                fontSize: '0.8125rem', fontWeight: 600, cursor: 'pointer',
                                opacity: (!nm.name.trim() || !nm.due_date) ? 0.5 : 1
                              }}
                            >Add</button>
                            <button
                              onClick={() => setAddingMilestoneTo(null)}
                              style={{
                                background: 'none', border: '1px solid var(--silver)',
                                color: 'var(--slate)', padding: '5px 10px', cursor: 'pointer'
                              }}
                            >✕</button>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Empty state row */}
                    {wsMilestones.length === 0 && !isAddingHere && (
                      <div style={{
                        height: ROW_H, position: 'relative',
                        borderBottom: '1px solid var(--green-light)',
                        background: 'var(--white)'
                      }}>
                        <GridLines height={ROW_H} />
                      </div>
                    )}

                    {/* Milestone rows */}
                    {wsMilestones.map((ms, msIdx) => {
                      const isOverdue = new Date(ms.due_date) < new Date() && ms.status !== 'complete'
                      const isEditing = editingMilestone === ms.id
                      const msX = dateToX(ms.due_date)
                      const dotSize = ms.is_key_milestone ? 14 : 9

                      return (
                        <div key={ms.id}>
                          <div style={{
                            height: ROW_H, position: 'relative',
                            background: msIdx % 2 === 0 ? 'var(--white)' : 'var(--off-white)',
                            borderBottom: '1px solid var(--green-light)'
                          }}>
                            <GridLines height={ROW_H} />
                            {/* Effort bar — only shown when start_date is set */}
                            {ms.start_date && (
                              <div style={{
                                position: 'absolute',
                                left: dateToX(ms.start_date),
                                top: '50%',
                                transform: 'translateY(-50%)',
                                width: Math.max(4, msX - dateToX(ms.start_date)),
                                height: 6,
                                background: ms.status === 'complete'
                                  ? 'var(--green)'
                                  : ms.status === 'missed' || isOverdue
                                  ? 'var(--red)'
                                  : ms.status === 'at_risk'
                                  ? '#E67E22'
                                  : `#${ws.colour}`,
                                opacity: 0.5,
                                borderRadius: '3px 0 0 3px',
                                zIndex: 2
                              }} />
                            )}
                            {/* Milestone marker */}
                            <div style={{
                              position: 'absolute',
                              left: msX - dotSize / 2,
                              top: '50%',
                              transform: ms.is_key_milestone
                                ? 'translateY(-50%) rotate(45deg)'
                                : 'translateY(-50%)',
                              width: dotSize, height: dotSize,
                              background: ms.status === 'complete'
                                ? 'var(--green)'
                                : ms.status === 'missed' || isOverdue
                                ? 'var(--red)'
                                : ms.status === 'at_risk'
                                ? '#E67E22'
                                : `#${ws.colour}`,
                              borderRadius: ms.is_key_milestone ? 0 : '50%',
                              boxShadow: ms.is_key_milestone
                                ? `0 0 0 2px white, 0 0 0 3.5px #${ws.colour}`
                                : '0 0 0 1.5px white',
                              zIndex: 3
                            }} />
                          </div>
                          {/* Edit date spacer */}
                          {isEditing && (
                            <div style={{
                              height: ROW_H, position: 'relative',
                              borderBottom: '1px solid var(--green-light)',
                              background: 'var(--green-pale)'
                            }}>
                              <GridLines height={ROW_H} />
                            </div>
                          )}
                        </div>
                      )
                    })}
                  </div>
                )
              })}

              {/* Footer */}
              <div style={{
                height: 20, position: 'relative',
                borderTop: '1px solid var(--green-light)',
                background: 'var(--off-white)'
              }}>
                {todayX >= 0 && todayX <= chartWidth && (
                  <div style={{
                    position: 'absolute', left: todayX, top: 3,
                    transform: 'translateX(-50%)',
                    fontSize: '0.5625rem', fontWeight: 700,
                    color: 'var(--gold)', letterSpacing: '0.06em',
                    textTransform: 'uppercase', whiteSpace: 'nowrap'
                  }}>▲ TODAY</div>
                )}
                <div style={{
                  position: 'absolute', left: submissionX, top: 3,
                  transform: 'translateX(-50%)',
                  fontSize: '0.5625rem', fontWeight: 700,
                  color: 'var(--green)', letterSpacing: '0.06em',
                  textTransform: 'uppercase', whiteSpace: 'nowrap'
                }}>▲ SUBMIT</div>
              </div>

            </div>
          </div>
        </div>

        {/* ── FROZEN DATE COLUMN ────────────────────────────────────────── */}
        <div style={{
          width: DATE_COL, minWidth: DATE_COL, flexShrink: 0,
          borderLeft: '1px solid var(--green-light)',
          borderRight: '2px solid var(--green)',
          zIndex: 10, background: 'var(--white)'
        }}>
          {/* Month header */}
          <div style={{
            height: HEADER_H1, background: 'var(--green-dark)',
            borderBottom: '1px solid var(--green)',
            display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}>
            <span style={{
              fontSize: '0.6875rem', fontWeight: 700,
              letterSpacing: '0.08em', textTransform: 'uppercase',
              color: 'var(--green-light)'
            }}>Date</span>
          </div>
          {/* Week header */}
          <div style={{
            height: HEADER_H2, background: 'var(--off-white)',
            borderBottom: '2px solid var(--green-light)'
          }} />

          {workstreams.map(ws => {
            const wsMilestones = milestones
              .filter(m => m.workstream_id === ws.id)
              .sort((a, b) => new Date(a.due_date) - new Date(b.due_date))
            const isAddingHere = addingMilestoneTo === ws.id
            return (
              <div key={ws.id}>
                {/* Workstream spacer */}
                <div style={{
                  height: ROW_H, background: `#${ws.colour}18`,
                  borderBottom: '1px solid var(--green-light)'
                }} />
                {isAddingHere && (
                  <div style={{
                    height: ROW_H + 8,
                    borderBottom: '1px solid var(--green-light)',
                    background: 'var(--green-pale)'
                  }} />
                )}
                {wsMilestones.length === 0 && !isAddingHere && (
                  <div style={{ height: ROW_H, borderBottom: '1px solid var(--green-light)' }} />
                )}
                {wsMilestones.map((ms, msIdx) => {
                  const isOverdue = new Date(ms.due_date) < new Date() && ms.status !== 'complete'
                  const isEditing = editingMilestone === ms.id
                  return (
                    <div key={ms.id}>
                      <div
                        style={{
                          height: ROW_H,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          padding: '0 6px',
                          background: msIdx % 2 === 0 ? 'var(--white)' : 'var(--off-white)',
                          borderBottom: '1px solid var(--green-light)',
                          cursor: 'pointer'
                        }}
                        title="Click to edit date"
                        onClick={() => setEditingMilestone(isEditing ? null : ms.id)}
                      >
                        <span style={{
                          fontSize: '0.75rem',
                          color: isEditing
                            ? 'var(--green)'
                            : isOverdue
                            ? 'var(--red)'
                            : 'var(--slate)',
                          fontWeight: isOverdue || isEditing ? 600 : 400,
                          textAlign: 'center', lineHeight: 1.2,
                          textDecoration: 'underline',
                          textDecorationStyle: 'dotted',
                          textDecorationColor: isOverdue ? 'var(--red)' : 'var(--silver)'
                        }}>
                          {formatDateShort(ms.due_date)}
                        </span>
                      </div>
                      {/* Inline date editor */}
                      {isEditing && (
                        <div style={{
                          height: ROW_H, borderBottom: '1px solid var(--green-light)',
                          background: 'var(--green-pale)',
                          display: 'flex', alignItems: 'center',
                          justifyContent: 'center', padding: '0 4px'
                        }}>
                          <input
                            type="date"
                            defaultValue={formatDateInput(ms.due_date)}
                            autoFocus
                            onBlur={async (e) => {
  if (e.target.value && e.target.value !== formatDateInput(ms.due_date)) {
    const newDate = new Date(e.target.value).toISOString()
    await supabase
      .from('milestones')
      .update({ due_date: newDate })
      .eq('id', ms.id)
    if (onMilestoneDateChanged) onMilestoneDateChanged(ms.id, newDate)
    setEditingMilestone(null)
  } else {
    setEditingMilestone(null)
  }
}}
                            onKeyDown={e => {
                              if (e.key === 'Enter') e.target.blur()
                              if (e.key === 'Escape') setEditingMilestone(null)
                            }}
                            style={{
                              width: '88px', padding: '4px 4px',
                              border: '1px solid var(--green)',
                              fontSize: '0.75rem', color: 'var(--green-dark)',
                              background: 'var(--white)', borderRadius: 0, outline: 'none'
                            }}
                          />
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            )
          })}
          <div style={{ height: 20, borderTop: '1px solid var(--green-light)', background: 'var(--off-white)' }} />
        </div>

        {/* ── RIGHT FROZEN — Status column ─────────────────────────────── */}
        <div style={{
          width: STATUS_COL, minWidth: STATUS_COL, flexShrink: 0,
          borderLeft: '2px solid var(--green)',
          zIndex: 10, background: 'var(--white)'
        }}>
          {/* Month header */}
          <div style={{
            height: HEADER_H1, background: 'var(--green-dark)',
            borderBottom: '1px solid var(--green)',
            display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}>
            <span style={{
              fontSize: '0.6875rem', fontWeight: 700,
              letterSpacing: '0.08em', textTransform: 'uppercase',
              color: 'var(--green-light)'
            }}>Status</span>
          </div>
          {/* Week header */}
          <div style={{
            height: HEADER_H2, background: 'var(--off-white)',
            borderBottom: '2px solid var(--green-light)'
          }} />

          {workstreams.map(ws => {
            const wsMilestones = milestones
              .filter(m => m.workstream_id === ws.id)
              .sort((a, b) => new Date(a.due_date) - new Date(b.due_date))
            const isAddingHere = addingMilestoneTo === ws.id

            return (
              <div key={ws.id}>
                {/* Workstream action buttons */}
                <div style={{
                  height: ROW_H, background: `#${ws.colour}18`,
                  borderBottom: '1px solid var(--green-light)',
                  display: 'flex', alignItems: 'center',
                  justifyContent: 'center', gap: 4, padding: '0 6px'
                }}>
                 <button
                    onClick={() => {
                      setAddingMilestoneTo(isAddingHere ? null : ws.id)
                      setNewMilestones(prev => ({ ...prev, [ws.id]: { name: '', start_date: '', due_date: '', is_key_milestone: false } }))
                    }}
                    style={{
                      background: isAddingHere ? `#${ws.colour}` : 'none',
                      border: `1px solid ${isAddingHere ? `#${ws.colour}` : 'var(--silver)'}`,
                      color: isAddingHere ? 'var(--white)' : 'var(--slate)',
                      padding: '2px 6px', fontSize: '0.625rem', fontWeight: 600,
                      cursor: 'pointer', whiteSpace: 'nowrap'
                    }}
                  >+ Add</button>
                  <button
                    onClick={() => onRemoveWorkstream(ws.id)}
                    style={{
                      background: 'none', border: '1px solid var(--silver)',
                      color: 'var(--slate)', padding: '2px 5px',
                      fontSize: '0.625rem', cursor: 'pointer'
                    }}
                  >✕</button>
                </div>

                {isAddingHere && (
                  <div style={{ height: ROW_H + 8, borderBottom: '1px solid var(--green-light)', background: 'var(--green-pale)' }} />
                )}
                {wsMilestones.length === 0 && !isAddingHere && (
                  <div style={{ height: ROW_H, borderBottom: '1px solid var(--green-light)' }} />
                )}

                {wsMilestones.map((ms, msIdx) => (
                  <div key={ms.id}>
                    <div style={{
                      height: ROW_H,
                      background: msIdx % 2 === 0 ? 'var(--white)' : 'var(--off-white)',
                      borderBottom: '1px solid var(--green-light)',
                      display: 'flex', alignItems: 'center', gap: 3, padding: '0 5px'
                    }}>
                      <select
                        value={ms.status}
                        onChange={e => onStatusUpdate(ms.id, e.target.value)}
                        style={{
                          flex: 1, padding: '3px 2px',
                          border: `1px solid ${getStatusColour(ms.status, ms.due_date)}`,
                          fontSize: '0.625rem', fontWeight: 600,
                          color: getStatusColour(ms.status, ms.due_date),
                          background: 'var(--white)', borderRadius: 0,
                          outline: 'none', cursor: 'pointer'
                        }}
                      >
                        <option value="pending">Pending</option>
                        <option value="in_progress">In Progress</option>
                        <option value="complete">Complete</option>
                        <option value="at_risk">At Risk</option>
                        <option value="missed">Missed</option>
                      </select>
                      <button
                        onClick={() => onDeleteMilestone(ms.id)}
                        title="Delete"
                        style={{
                          background: 'none', border: '1px solid var(--silver)',
                          color: 'var(--slate)', width: 20, height: 20,
                          fontSize: '0.6875rem', cursor: 'pointer',
                          display: 'flex', alignItems: 'center',
                          justifyContent: 'center', flexShrink: 0
                        }}
                        onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--red)'; e.currentTarget.style.color = 'var(--red)' }}
                        onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--silver)'; e.currentTarget.style.color = 'var(--slate)' }}
                      >✕</button>
                    </div>
                    {editingMilestone === ms.id && (
                      <div style={{ height: ROW_H, borderBottom: '1px solid var(--green-light)', background: 'var(--green-pale)' }} />
                    )}
                  </div>
                ))}
              </div>
            )
          })}
          <div style={{ height: 20, borderTop: '1px solid var(--green-light)', background: 'var(--off-white)' }} />
        </div>

      </div>
    </div>
  )
}
  

// ── Main BidPlan component ─────────────────────────────────────────────────
function BidPlan({ session, profile, onNavigate, linkedOpportunity, onOpenArtefacts, onOpenActions, onOpenQuestions, onOpenCqs }) {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const [view, setView] = useState(linkedOpportunity?.planId ? 'gantt_loading' : linkedOpportunity && !linkedOpportunity.planId ? 'create' : 'list')
  const [bidPlans, setBidPlans] = useState([])
  const [loading, setLoading] = useState(false)
  const [currentPlan, setCurrentPlan] = useState(null)
  const [workstreams, setWorkstreams] = useState([])
  const [milestones, setMilestones] = useState([])
  const [step, setStep] = useState('details')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [newWorkstream, setNewWorkstream] = useState('')
  const [editingMilestone, setEditingMilestone] = useState(null)
  const [editingPlan, setEditingPlan] = useState(false)
  const [editPlanDetails, setEditPlanDetails] = useState({})
  const [recalculating, setRecalculating] = useState(false)
  const [newMilestones, setNewMilestones] = useState({})
  const [addingMilestoneTo, setAddingMilestoneTo] = useState(null)

 const [planDetails, setPlanDetails] = useState({
    title: linkedOpportunity?.title || '',
    start_date: '',
    submission_deadline: linkedOpportunity?.deadline
      ? new Date(linkedOpportunity.deadline).toISOString().split('T')[0]
      : '',
    result_expected: ''
  })

  const [rules, setRules] = useState({
    rule_kickoff: 30, rule_solution_review: 20, rule_cq_deadline: 15,
    rule_technical_review: 14, rule_commercial_review: 12, rule_red_team: 10,
    rule_tech_review_to_final_approval: 5, rule_final_approval: 5,
  })

  const [activeWorkstreams, setActiveWorkstreams] = useState(
    DEFAULT_WORKSTREAMS.map(w => ({ ...w, active: true }))
  )

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (linkedOpportunity?.planId) {
      loadSpecificPlan(linkedOpportunity.planId)
    }
  }, [])

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (view === 'list' && bidPlans.length === 0 && !loading) {
      loadBidPlans()
    }
  }, [view])

  const loadSpecificPlan = async (planId) => {
    const { data: plan } = await supabase
      .from('bid_plans')
      .select('*')
      .eq('id', planId)
      .single()
    if (plan) {
      setCurrentPlan({ ...plan, start_date: plan.start_date })
      await loadPlanDetail(plan.id)
      setView('gantt')
    } else {
      setView('list')
    }
  }
  const loadBidPlans = async () => {
    setLoading(true)
    const { data } = await supabase.from('bid_plans').select('*').order('created_at', { ascending: false })
    setBidPlans(data || [])
    setLoading(false)
  }

  const loadPlanDetail = async (planId) => {
    const { data: ws } = await supabase.from('workstreams').select('*').eq('bid_plan_id', planId).order('sort_order')
    const { data: ms } = await supabase.from('milestones').select('*').eq('bid_plan_id', planId).order('due_date')
    setWorkstreams(ws || [])
    setMilestones(ms || [])
  }

  const generateMilestoneDates = (submissionDeadline, activeWs) => {
    const deadline = new Date(submissionDeadline)
    const generated = []
    activeWs.forEach(ws => {
      if (!ws.active) return
      const defaultMs = DEFAULT_MILESTONES[ws.name] || []
      defaultMs.forEach(ms => {
        let dueDate
        if (ms.rule_days === 0) dueDate = deadline
        else if (ms.rule_days < 0) dueDate = addWorkingDays(deadline, Math.abs(ms.rule_days))
        else dueDate = subtractWorkingDays(deadline, ms.rule_days)
        generated.push({ workstream_name: ws.name, name: ms.name, due_date: dueDate.toISOString(), is_key_milestone: ms.is_key_milestone, sort_order: ms.sort_order, status: 'pending' })
      })
    })
    return generated
  }

  const handleCreatePlan = async () => {
    setSaving(true)
    setError(null)
    try {
const { data: plan, error: planError } = await supabase
        .from('bid_plans')
        .insert([{
          title: planDetails.title,
          submission_deadline: planDetails.submission_deadline,
          result_expected: planDetails.result_expected || null,
          opportunity_id: linkedOpportunity?.id || null,
          organisation_id: profile?.organisation_id || null,
          created_by: session.user.id,
          status: 'active'
        }])
        .select()
        .single()
      if (planError) throw planError

      const wsToCreate = activeWorkstreams.filter(w => w.active).map(w => ({
        bid_plan_id: plan.id, name: w.name, colour: w.colour, sort_order: w.sort_order, is_active: true
      }))
      const { data: createdWs, error: wsError } = await supabase.from('workstreams').insert(wsToCreate).select()
      if (wsError) throw wsError

      const generatedMs = generateMilestoneDates(planDetails.submission_deadline, activeWorkstreams)
      const msToCreate = generatedMs.map(ms => {
        const ws = createdWs.find(w => w.name === ms.workstream_name)
        return { bid_plan_id: plan.id, workstream_id: ws?.id, name: ms.name, due_date: ms.due_date, is_key_milestone: ms.is_key_milestone, sort_order: ms.sort_order, status: 'pending' }
      })
      const { error: msError } = await supabase.from('milestones').insert(msToCreate)
      if (msError) throw msError

      setCurrentPlan({ ...plan, start_date: planDetails.start_date })
      await loadPlanDetail(plan.id)
      setView('gantt')
      setStep('details')
    } catch (err) {
      setError(err.message)
    } finally {
      setSaving(false)
    }
  }

  const handleUpdatePlan = async () => {
    setRecalculating(true)
    const deadlineChanged = editPlanDetails.submission_deadline !== formatDateInput(currentPlan.submission_deadline)
    const { data: updatedPlan, error: planError } = await supabase.from('bid_plans').update({
      title: editPlanDetails.title, submission_deadline: editPlanDetails.submission_deadline,
      result_expected: editPlanDetails.result_expected || null
    }).eq('id', currentPlan.id).select().single()
    if (planError) { setError(planError.message); setRecalculating(false); return }
    setCurrentPlan({ ...updatedPlan, start_date: editPlanDetails.start_date })
    if (deadlineChanged) {
      const newDeadline = new Date(editPlanDetails.submission_deadline)
      const updates = milestones.map(ms => {
        const ws = workstreams.find(w => w.id === ms.workstream_id)
        if (!ws) return ms
        const defaultWsMs = DEFAULT_MILESTONES[ws.name] || []
        const defaultMs = defaultWsMs.find(d => d.name === ms.name)
        if (!defaultMs) return ms
        let newDate
        if (defaultMs.rule_days === 0) newDate = newDeadline
        else if (defaultMs.rule_days < 0) newDate = addWorkingDays(newDeadline, Math.abs(defaultMs.rule_days))
        else newDate = subtractWorkingDays(newDeadline, defaultMs.rule_days)
        return { ...ms, due_date: newDate.toISOString() }
      })
      await Promise.all(updates.map(ms => supabase.from('milestones').update({ due_date: ms.due_date }).eq('id', ms.id)))
      setMilestones([...updates].sort((a, b) => new Date(a.due_date) - new Date(b.due_date)))
    }
    setEditingPlan(false)
    setRecalculating(false)
  }

  const handleStatusUpdate = async (milestoneId, newStatus) => {
    const completedAt = newStatus === 'complete' ? new Date().toISOString() : null
    await supabase.from('milestones').update({ status: newStatus, completed_at: completedAt }).eq('id', milestoneId)
    setMilestones(prev => prev.map(m => m.id === milestoneId ? { ...m, status: newStatus, completed_at: completedAt } : m))
  }

  const handleAddWorkstream = async () => {
    if (!newWorkstream.trim() || !currentPlan) return
    const { data, error } = await supabase.from('workstreams').insert([{
      bid_plan_id: currentPlan.id, name: newWorkstream.trim(),
      colour: '4A5568', sort_order: workstreams.length, is_active: true
    }]).select().single()
    if (!error && data) {
      setWorkstreams(prev => [...prev, data])
      setNewWorkstream('')
      setNewMilestones(prev => ({ ...prev, [data.id]: { name: '', due_date: '' } }))
    }
  }

  const handleRemoveWorkstream = async (wsId) => {
    await supabase.from('workstreams').delete().eq('id', wsId)
    setWorkstreams(prev => prev.filter(w => w.id !== wsId))
    setMilestones(prev => prev.filter(m => m.workstream_id !== wsId))
  }

  const handleAddMilestone = async (wsId) => {
    const nm = newMilestones[wsId]
    if (!nm || !nm.name.trim() || !nm.due_date) return
    const wsMilestones = milestones.filter(m => m.workstream_id === wsId)
    const { data, error: insertError } = await supabase.from('milestones').insert([{
      bid_plan_id: currentPlan.id, workstream_id: wsId,
      name: nm.name.trim(),
      start_date: nm.start_date ? new Date(nm.start_date).toISOString() : null,
      due_date: new Date(nm.due_date).toISOString(),
      is_key_milestone: nm.is_key_milestone || false,
      sort_order: wsMilestones.length, status: 'pending'
    }]).select().single()
    if (!insertError && data) {
      setMilestones(prev =>
        [...prev, data].sort((a, b) => new Date(a.due_date) - new Date(b.due_date))
      )
      setNewMilestones(prev => ({ ...prev, [wsId]: { name: '', start_date: '', due_date: '', is_key_milestone: false } }))
      setAddingMilestoneTo(null)
    }
  }

  const handleDeleteMilestone = async (msId) => {
    await supabase.from('milestones').delete().eq('id', msId)
    setMilestones(prev => prev.filter(m => m.id !== msId))
  }

  // ── List view ──────────────────────────────────────────────────────────────
  if (view === 'list') {
    return (
      <div className="scorecard-page container">
        <div className="page-header">
          <p className="label">Bid Plan</p>
          <h1>Bid Plans</h1>
          <p>Generate structured bid plans with automatic milestone scheduling based on your submission deadline.</p>
        </div>
        <div style={{ marginBottom: '24px' }}>
          <button className="btn btn--primary" onClick={() => setView('create')}>+ Create New Bid Plan</button>
        </div>
        {loading && <p style={{ color: 'var(--slate)' }}>Loading...</p>}
        {!loading && bidPlans.length === 0 && (
          <div className="card"><p style={{ color: 'var(--slate)', textAlign: 'center', padding: '24px 0' }}>No bid plans yet. Create your first one to get started.</p></div>
        )}
        {bidPlans.map(plan => {
          const daysUntil = getDaysUntil(plan.submission_deadline)
          return (
            <div key={plan.id} className="card" style={{ cursor: 'pointer', marginBottom: '12px' }}
              onClick={async () => { setCurrentPlan(plan); await loadPlanDetail(plan.id); setView('gantt') }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1.25rem', fontWeight: 600, color: 'var(--green-dark)', marginBottom: '4px' }}>{plan.title}</div>
                  <div style={{ fontSize: '0.875rem', color: 'var(--slate)' }}>
                    {plan.start_date && `Start: ${formatDate(plan.start_date)} · `}Submission: {formatDate(plan.submission_deadline)}
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: '0.75rem', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: daysUntil < 7 ? 'var(--red)' : daysUntil < 14 ? 'var(--amber)' : 'var(--green)', marginBottom: '4px' }}>
                    {daysUntil < 0 ? 'Submitted' : daysUntil === 0 ? 'Due today' : `${daysUntil} days remaining`}
                  </div>
                  <div style={{ display: 'inline-block', padding: '3px 10px', background: 'var(--green-pale)', fontSize: '0.75rem', fontWeight: 600, color: 'var(--green)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{plan.status}</div>
                </div>
              </div>
            </div>
          )
        })}
        <div className="actions">
          <button className="btn btn--secondary" onClick={() => onNavigate('dashboard')}>← Dashboard</button>
        </div>
      </div>
    )
  }

  // ── Create step 1 ──────────────────────────────────────────────────────────
  if (view === 'create' && step === 'details') {
    return (
      <div className="scorecard-page container">
        <div className="page-header"><p className="label">Bid Plan</p><h1>Create New Bid Plan</h1><p>Enter the bid details and we will generate a structured plan automatically.</p></div>
        <div className="card">
          <div className="card__header"><span className="card__title">Bid Details</span><span className="card__weight">Step 1 of 3</span></div>
          <div className="form-group">
            <label>Bid / Opportunity Title *</label>
            <input type="text" value={planDetails.title} onChange={e => setPlanDetails({ ...planDetails, title: e.target.value })} placeholder="e.g. MOD Logistics Support Framework" />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
            <div className="form-group"><label>Bid Start Date *</label><input type="date" value={planDetails.start_date} onChange={e => setPlanDetails({ ...planDetails, start_date: e.target.value })} /></div>
            <div className="form-group"><label>Submission Deadline *</label><input type="date" value={planDetails.submission_deadline} onChange={e => setPlanDetails({ ...planDetails, submission_deadline: e.target.value })} /></div>
            <div className="form-group"><label>Result Expected (optional)</label><input type="date" value={planDetails.result_expected} onChange={e => setPlanDetails({ ...planDetails, result_expected: e.target.value })} /></div>
          </div>
          {planDetails.start_date && planDetails.submission_deadline && (() => {
            const days = Math.ceil((new Date(planDetails.submission_deadline) - new Date(planDetails.start_date)) / (1000 * 60 * 60 * 24))
            return <div style={{ marginTop: '8px', padding: '12px 16px', background: 'var(--green-pale)', borderLeft: '3px solid var(--green)', fontSize: '0.875rem', color: 'var(--green-dark)' }}>{days > 0 ? `Bid duration: ${days} calendar days.` : '⚠ Submission deadline must be after start date.'}</div>
          })()}
        </div>
        <div className="actions">
          <button className="btn btn--primary" onClick={() => setStep('workstreams')} disabled={!planDetails.title || !planDetails.start_date || !planDetails.submission_deadline || new Date(planDetails.submission_deadline) <= new Date(planDetails.start_date)}>Next — Configure Workstreams →</button>
          <button
            className="btn btn--secondary"
            onClick={() => linkedOpportunity ? onNavigate('opportunities') : setView('list')}
          >
            Cancel
          </button>
        </div>
      </div>
    )
  }

  // ── Create step 2 ──────────────────────────────────────────────────────────
  if (view === 'create' && step === 'workstreams') {
    return (
      <div className="scorecard-page container">
        <div className="page-header"><p className="label">Bid Plan</p><h1>Configure Workstreams</h1><p>Select the workstreams relevant to this bid.</p></div>
        <div className="card">
          <div className="card__header"><span className="card__title">Workstreams</span><span className="card__weight">Step 2 of 3</span></div>
          {activeWorkstreams.map((ws, i) => (
            <div key={ws.name} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 0', borderBottom: '1px solid var(--green-light)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{ width: '12px', height: '12px', background: `#${ws.colour}`, flexShrink: 0 }} />
                <span style={{ fontWeight: 600, color: ws.active ? 'var(--charcoal)' : 'var(--silver)', fontSize: '0.9375rem' }}>{ws.name}</span>
                <span style={{ fontSize: '0.75rem', color: 'var(--slate)' }}>{(DEFAULT_MILESTONES[ws.name] || []).length} milestones</span>
              </div>
              <button onClick={() => { const u = [...activeWorkstreams]; u[i].active = !u[i].active; setActiveWorkstreams(u) }}
                style={{ background: ws.active ? 'var(--green)' : 'var(--silver)', border: 'none', color: 'var(--white)', padding: '5px 14px', fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', cursor: 'pointer' }}>
                {ws.active ? 'Active' : 'Off'}
              </button>
            </div>
          ))}
        </div>
        <div className="actions">
          <button className="btn btn--primary" onClick={() => setStep('rules')}>Next — Configure Planning Rules →</button>
          <button className="btn btn--secondary" onClick={() => setStep('details')}>← Back</button>
        </div>
      </div>
    )
  }

  // ── Create step 3 ──────────────────────────────────────────────────────────
  if (view === 'create' && step === 'rules') {
    return (
      <div className="scorecard-page container">
        <div className="page-header"><p className="label">Bid Plan</p><h1>Planning Rules</h1><p>Set working days before submission for each key milestone.</p></div>
        <div className="card">
          <div className="card__header"><span className="card__title">Milestone Rules</span><span className="card__weight">Step 3 of 3</span></div>
          {[
            { key: 'rule_kickoff', label: 'Kick-off Meeting' },
            { key: 'rule_solution_review', label: 'Solution Review' },
            { key: 'rule_cq_deadline', label: 'Clarification Questions Deadline' },
            { key: 'rule_technical_review', label: 'Technical Review' },
            { key: 'rule_commercial_review', label: 'Commercial Review' },
            { key: 'rule_red_team', label: 'Red Team Review' },
            { key: 'rule_final_approval', label: 'Final Approval' },
            { key: 'rule_tech_review_to_final_approval', label: 'Min days — Technical Review to Final Approval' },
          ].map(rule => (
            <div key={rule.key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid var(--green-light)' }}>
              <span style={{ fontSize: '0.9375rem', color: 'var(--charcoal)', fontWeight: 500 }}>{rule.label}</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <input type="number" min="1" max="60" value={rules[rule.key]} onChange={e => setRules({ ...rules, [rule.key]: parseInt(e.target.value) || 1 })}
                  style={{ width: '64px', padding: '6px 10px', border: '1px solid var(--silver)', fontSize: '1rem', fontWeight: 600, color: 'var(--green-dark)', textAlign: 'center', borderRadius: 0, outline: 'none' }} />
                <span style={{ fontSize: '0.875rem', color: 'var(--slate)' }}>working days before submission</span>
              </div>
            </div>
          ))}
        </div>
        {error && <div className="error-msg">{error}</div>}
        <div className="actions">
          <button className="btn btn--primary" onClick={handleCreatePlan} disabled={saving}>{saving ? 'Generating...' : 'Generate Bid Plan →'}</button>
          <button className="btn btn--secondary" onClick={() => setStep('workstreams')}>← Back</button>
        </div>
      </div>
    )
  }

  // ── Gantt view ─────────────────────────────────────────────────────────────
  if (view === 'gantt' && currentPlan) {
    const daysUntilSubmission = getDaysUntil(currentPlan.submission_deadline)

    const chartStart = currentPlan.start_date
      ? new Date(currentPlan.start_date)
      : milestones.length > 0
      ? new Date(Math.min(...milestones.map(m => new Date(m.due_date))))
      : new Date()

    const chartEnd = currentPlan.result_expected
      ? new Date(currentPlan.result_expected)
      : new Date(currentPlan.submission_deadline)

    return (
      <div style={{ padding: '24px' }}>

        {/* Plan header */}
        <div style={{ background: 'var(--green-dark)', padding: '20px 24px', marginBottom: '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '0.6875rem', fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--green-light)', marginBottom: '6px' }}>Bid Plan</div>
              {editingPlan ? (
                <input value={editPlanDetails.title} onChange={e => setEditPlanDetails({ ...editPlanDetails, title: e.target.value })}
                  style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.3)', color: 'var(--white)', padding: '8px 12px', fontSize: '1.375rem', fontFamily: 'Cormorant Garamond, serif', fontWeight: 600, borderRadius: 0, outline: 'none', width: '100%', maxWidth: 500 }} />
              ) : (
                <div style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1.75rem', fontWeight: 600, color: 'var(--white)' }}>{currentPlan.title}</div>
              )}
              <div style={{ fontSize: '0.875rem', color: 'var(--silver)', marginTop: '6px' }}>
                {editingPlan ? (
                  <div style={{ display: 'flex', gap: '16px', marginTop: '8px', flexWrap: 'wrap' }}>
                    {[{ key: 'start_date', label: 'Start Date' }, { key: 'submission_deadline', label: 'Submission Deadline' }, { key: 'result_expected', label: 'Result Expected' }].map(f => (
                      <div key={f.key}>
                        <div style={{ fontSize: '0.6875rem', color: 'var(--green-light)', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.08em' }}>{f.label}</div>
                        <input type="date" value={editPlanDetails[f.key] || ''} onChange={e => setEditPlanDetails({ ...editPlanDetails, [f.key]: e.target.value })}
                          style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.3)', color: 'var(--white)', padding: '6px 10px', fontSize: '0.875rem', borderRadius: 0, outline: 'none', colorScheme: 'dark' }} />
                      </div>
                    ))}
                  </div>
                ) : (
                  <>
                    {currentPlan.start_date && `Start: ${formatDate(currentPlan.start_date)} · `}
                    Submission: {formatDate(currentPlan.submission_deadline)}
                    {currentPlan.result_expected && ` · Result: ${formatDate(currentPlan.result_expected)}`}
                  </>
                )}
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '12px', marginLeft: '24px' }}>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '3rem', fontWeight: 700, color: daysUntilSubmission < 7 ? 'var(--red)' : daysUntilSubmission < 14 ? 'var(--gold)' : 'var(--white)', lineHeight: 1 }}>
                  {daysUntilSubmission < 0 ? '✓' : daysUntilSubmission}
                </div>
                <div style={{ fontSize: '0.6875rem', fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--green-light)' }}>
                  {daysUntilSubmission < 0 ? 'Submitted' : 'Days to submission'}
                </div>
              </div>
              {editingPlan ? (
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button onClick={handleUpdatePlan} disabled={recalculating}
                    style={{ background: 'var(--green)', border: 'none', color: 'var(--white)', padding: '8px 16px', fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', cursor: 'pointer' }}>
                    {recalculating ? 'Saving...' : 'Save Changes'}
                  </button>
                  <button onClick={() => setEditingPlan(false)}
                    style={{ background: 'none', border: '1px solid rgba(255,255,255,0.3)', color: 'var(--silver)', padding: '8px 16px', fontSize: '0.75rem', textTransform: 'uppercase', cursor: 'pointer' }}>
                    Cancel
                  </button>
                </div>
              ) : (
                <button onClick={() => { setEditPlanDetails({ title: currentPlan.title, start_date: formatDateInput(currentPlan.start_date), submission_deadline: formatDateInput(currentPlan.submission_deadline), result_expected: formatDateInput(currentPlan.result_expected) }); setEditingPlan(true) }}
                  style={{ background: 'none', border: '1px solid rgba(255,255,255,0.3)', color: 'var(--silver)', padding: '6px 14px', fontSize: '0.75rem', textTransform: 'uppercase', cursor: 'pointer' }}>
                  Edit Plan
                </button>
              )}
              {linkedOpportunity && (
                <div style={{ display: 'flex', gap: '8px', marginTop: '12px', flexWrap: 'wrap' }}>
                  {[
                    { label: 'Artefacts', handler: () => onOpenArtefacts && onOpenArtefacts(linkedOpportunity) },
                    { label: 'Actions', handler: () => onOpenActions && onOpenActions(linkedOpportunity) },
                    { label: 'Response Questions', handler: () => onOpenQuestions && onOpenQuestions(linkedOpportunity) },
                    { label: 'CQs', handler: () => onOpenCqs && onOpenCqs(linkedOpportunity) },
                  ].map(btn => (
                    <button
                      key={btn.label}
                      onClick={btn.handler}
                      style={{
                        background: 'rgba(255,255,255,0.08)',
                        border: '1px solid rgba(255,255,255,0.25)',
                        color: 'rgba(255,255,255,0.8)',
                        padding: '4px 12px', fontSize: '0.75rem',
                        fontWeight: 600, letterSpacing: '0.04em',
                        cursor: 'pointer', transition: 'all 0.15s'
                      }}
                      onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.15)'; e.currentTarget.style.color = 'var(--white)' }}
                      onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; e.currentTarget.style.color = 'rgba(255,255,255,0.8)' }}
                    >
                      {btn.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Gantt chart */}
        <GanttTimeline
          key={milestones.map(m => m.id + m.due_date).join('-')}
          workstreams={workstreams}
          milestones={milestones}
          chartStart={chartStart}
          chartEnd={chartEnd}
          onStatusUpdate={handleStatusUpdate}
          onDeleteMilestone={handleDeleteMilestone}
          onAddMilestone={handleAddMilestone}
          onRemoveWorkstream={handleRemoveWorkstream}
          editingMilestone={editingMilestone}
          setEditingMilestone={setEditingMilestone}
          onMilestoneDateChanged={(msId, newDate) => {
            setMilestones(prev => {
              const updated = prev.map(m =>
                m.id === msId ? { ...m, due_date: newDate } : m
              )
              return [...updated].sort((a, b) => new Date(a.due_date) - new Date(b.due_date))
            })
          }}
          addingMilestoneTo={addingMilestoneTo}
          setAddingMilestoneTo={setAddingMilestoneTo}
          newMilestones={newMilestones}
          setNewMilestones={setNewMilestones}
        />

        {/* Legend */}
        <div style={{ display: 'flex', gap: '20px', margin: '16px 0', padding: '10px 16px', background: 'var(--off-white)', border: '1px solid var(--green-light)', flexWrap: 'wrap' }}>
          {[
            { colour: 'var(--green)', label: 'Complete', shape: 'circle' },
            { colour: 'var(--gold)', label: 'In Progress', shape: 'circle' },
            { colour: '#E67E22', label: 'At Risk', shape: 'circle' },
            { colour: 'var(--red)', label: 'Overdue / Missed', shape: 'circle' },
            { colour: 'var(--silver)', label: 'Pending', shape: 'circle' },
            { colour: 'var(--gold)', label: 'Today', shape: 'line' },
            { colour: 'var(--green)', label: 'Submission', shape: 'line' },
          ].map((item, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              {item.shape === 'circle'
                ? <div style={{ width: 8, height: 8, borderRadius: '50%', background: item.colour, flexShrink: 0 }} />
                : <div style={{ width: 2, height: 14, background: item.colour, flexShrink: 0 }} />}
              <span style={{ fontSize: '0.75rem', color: 'var(--slate)' }}>{item.label}</span>
            </div>
          ))}
        </div>

        {/* Add workstream */}
        <div className="card" style={{ marginBottom: '16px' }}>
          <div className="card__header"><span className="card__title">Add Workstream</span></div>
          <div style={{ display: 'flex', gap: '12px' }}>
            <input type="text" value={newWorkstream} onChange={e => setNewWorkstream(e.target.value)}
              placeholder="e.g. Partnerships, Subcontractors, Security Clearance"
              style={{ flex: 1, padding: '10px 14px', border: '1px solid var(--silver)', fontSize: '0.9375rem', borderRadius: 0, outline: 'none' }}
              onKeyDown={e => e.key === 'Enter' && handleAddWorkstream()} />
            <button className="btn btn--primary" onClick={handleAddWorkstream} disabled={!newWorkstream.trim()}>Add</button>
          </div>
        </div>

        <div className="actions">
          <button className="btn btn--secondary" onClick={() => { setView('list'); loadBidPlans() }}>← All Bid Plans</button>
          <button className="btn btn--secondary" onClick={() => onNavigate('dashboard')}>Dashboard</button>
        </div>
      </div>
    )
  }

  return null
}

export default BidPlan